import React, { useState, useEffect } from "react";
import SEO from "../seo";
const GetPayments = () => {
  const [activeSection, setActiveSection] = useState("overview");

  useEffect(() => {
    const handleScroll = () => {
      const sections = [
        "overview",
        "available-payment-methods",
        "setup-payment-methods",
        "managing-payments",
        "security-compliance",
        "common-issues-tips",
        "summary",
        "process-payments-overview",
        "payment-processing-workflow",
        "payment-flows-setups",
        "handling-payment-failures",
        "settlement-dispute-management",
        "security-compliance-2",
        "summary-2",
      ];

      const scrollPosition = window.scrollY + 100;

      for (const sectionId of sections) {
        const element = document.getElementById(sectionId);
        if (element) {
          const offsetTop = element.offsetTop;
          const offsetHeight = element.offsetHeight;

          if (
            scrollPosition >= offsetTop &&
            scrollPosition < offsetTop + offsetHeight
          ) {
            setActiveSection(sectionId);
            break;
          }
        }
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <div className="flex flex-col lg:flex-row gap-6">
      <SEO
        title="Get Payments | xPage Help Center"
        description="Learn how to accept payments on xPage. Set up payment methods, process transactions, manage refunds and disputes, and ensure secure payment handling."
        keywords="xPage, payments, payment setup, payment processing, payment methods, refunds, disputes, secure payments, digital wallets, credit cards, PayPal, Stripe"
        canonical="https://help.xpage.ai/help/store-operation/Get%20Payments"
        type="article"
      />
      {/* Main Content */}
      <div className="flex-1 prose max-w-none">
        {/* Header Section */}
        <header className="mb-6">
          <h1 className="text-systemTitle text-2xl font-semibold mb-3">
            Get payments with xPage
          </h1>
          <p className="text-sm text-systemLabel">
            xPage offers seamless, secure payment handling so you can focus on
            running your business. From setup to payouts, this section walks you
            through configuring payment methods, processing orders, and managing
            transactions effectively.
          </p>
        </header>

        {/* Available Payment Methods Section */}
        <section id="available-payment-methods" className="mb-6 scroll-mt-20">
          <h2 className="text-sm font-semibold text-systemTitle mb-1">
            Available payment methods
          </h2>

          <div className="bg-systemBackground border border-systemBorder rounded-lg p-4 mt-2">
            <p className="text-sm text-systemLabel mb-4">
              xPage supports a variety of payment methods to suit regional needs
              and preferences:
            </p>

            <table className="w-full mb-6">
              <tbody className="border divide-y divide-systemBorder">
                <tr className="bg-gray-400/10 transition-colors">
                  <td className="p-3 align-top w-1/3 border-r border-systemBorder">
                    <strong className="text-systemTitle text-sm">
                      Payment Method
                    </strong>
                  </td>
                  <td className="p-3 align-top text-systemLabel text-sm">
                    Description
                  </td>
                </tr>
                <tr className="">
                  <td className="p-3 align-top w-1/3 border-r border-systemBorder">
                    <strong className="text-systemTitle text-sm">
                      Credit & debit card payments
                    </strong>
                  </td>
                  <td className="p-3 align-top text-systemLabel text-sm">
                    Accept major card brands like Visa, Mastercard, and American
                    Express.
                  </td>
                </tr>
                <tr className="bg-gray-400/10 transition-colors">
                  <td className="p-3 align-top w-1/3 border-r border-systemBorder">
                    <strong className="text-systemTitle text-sm">
                      Digital wallets
                    </strong>
                  </td>
                  <td className="p-3 align-top text-systemLabel text-sm">
                    Support wallets such as Apple Pay and Google Pay for fast
                    checkout.
                  </td>
                </tr>
                <tr className="">
                  <td className="p-3 align-top w-1/3 border-r border-systemBorder">
                    <strong className="text-systemTitle text-sm">
                      Third-party gateways
                    </strong>
                  </td>
                  <td className="p-3 align-top text-systemLabel text-sm">
                    Integrate with external gateways like Stripe or PayPal,
                    depending on what's configured for your store.
                  </td>
                </tr>
                <tr className="bg-gray-400/10 transition-colors">
                  <td className="p-3 align-top w-1/3 border-r border-systemBorder">
                    <strong className="text-systemTitle text-sm">
                      Cash on Delivery
                    </strong>
                  </td>
                  <td className="p-3 align-top text-systemLabel text-sm">
                    Enable COD where appropriate for flexible consumer choice.
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </section>

        {/* Set Up Payment Methods Section */}
        <section id="setup-payment-methods" className="mb-6 scroll-mt-20">
          <h2 className="text-sm font-semibold text-systemTitle mb-1">
            Set up payment methods
          </h2>

          <div className="bg-systemBackground border border-systemBorder rounded-lg p-4 mt-2">
            <p className="text-sm text-systemLabel mb-4">
              To accept payments in xPage:
            </p>

            <ol className="list-decimal list-inside space-y-1 text-sm text-systemLabel ml-4">
              <li className="mb-2">
                <span className="font-medium">Log in to the xPage admin.</span>
              </li>
              <li className="mb-2">
                <span className="font-medium">
                  Navigate to Settings → Payments (or Billing & Subscription).
                </span>
              </li>
              <li className="mb-2">
                <span className="font-medium">Choose your payment method:</span>
              </li>
            </ol>
          </div>
        </section>

        {/* Managing Payments Section */}
        <section id="managing-payments" className="mb-6 scroll-mt-20">
          <h2 className="text-sm font-semibold text-systemTitle mb-1">
            Managing payments and transactions
          </h2>

          <div className="bg-systemBackground border border-systemBorder rounded-lg p-4 mt-2">
            <p className="text-sm text-systemLabel mb-4">Once integrated:</p>

            <ul className="list-disc list-inside space-y-1 text-sm text-systemLabel ml-4">
              <li>
                Customers can pay using your configured method(s) at checkout.
              </li>
              <li>
                Transaction tracking is visible directly in your admin
                interface; you can view payment status, issue refunds, and more.
              </li>
              <li>
                Payout processing occurs according to the schedule set in your
                payment provider configuration; pending settlements will be
                handled as usual.
              </li>
            </ul>
          </div>
        </section>

        {/* Security and Compliance Section */}
        <section id="security-compliance" className="mb-6 scroll-mt-20">
          <h2 className="text-sm font-semibold text-systemTitle mb-1">
            Security and compliance
          </h2>

          <div className="bg-systemBackground border border-systemBorder rounded-lg p-4 mt-2">
            <p className="text-sm text-systemLabel mb-4">
              xPage ensures safe payment processing:
            </p>

            <ul className="list-disc list-inside space-y-1 text-sm text-systemLabel ml-4">
              <li>
                <strong>PCI-compliant:</strong> Whether you use hosted checkout
                pages or integrated gateways, sensitive payment data is handled
                securely.
              </li>
              <li>
                <strong>SSL encryption:</strong> Your store's URLs are served
                over HTTPS for safe browsing and checkout.
              </li>
            </ul>
          </div>
        </section>

        {/* Common Issues & Tips Section */}
        <section id="common-issues-tips" className="mb-6 scroll-mt-20">
          <h2 className="text-sm font-semibold text-systemTitle mb-1">
            Common issues & tips
          </h2>

          <div className="bg-systemBackground border border-systemBorder rounded-lg p-4 mt-2">
            <table className="w-full mb-6">
              <tbody className="border divide-y divide-systemBorder">
                <tr className="bg-gray-400/10 transition-colors">
                  <td className="p-3 align-top w-1/3 border-r border-systemBorder">
                    <strong className="text-systemTitle text-sm">
                      Scenario
                    </strong>
                  </td>
                  <td className="p-3 align-top text-systemLabel text-sm">
                    <strong className="text-systemTitle text-sm">
                      What Happens
                    </strong>
                  </td>
                  <td className="p-3 align-top text-systemLabel text-sm">
                    <strong className="text-systemTitle text-sm">Tip</strong>
                  </td>
                </tr>
                <tr className="">
                  <td className="p-3 align-top w-1/3 border-r border-systemBorder">
                    <strong className="text-systemTitle text-sm">
                      Failed payments
                    </strong>
                  </td>
                  <td className="p-3 align-top text-systemLabel text-sm">
                    Payments fail due to card or gateway issues.
                  </td>
                  <td className="p-3 align-top text-systemLabel text-sm">
                    Promptly check and resolve or retry.
                  </td>
                </tr>
                <tr className="bg-gray-400/10 transition-colors">
                  <td className="p-3 align-top w-1/3 border-r border-systemBorder">
                    <strong className="text-systemTitle text-sm">
                      Payment refunds
                    </strong>
                  </td>
                  <td className="p-3 align-top text-systemLabel text-sm">
                    May not be supported in all gateways.
                  </td>
                  <td className="p-3 align-top text-systemLabel text-sm">
                    Check app capabilities or gateway settings.
                  </td>
                </tr>
                <tr className="">
                  <td className="p-3 align-top w-1/3 border-r border-systemBorder">
                    <strong className="text-systemTitle text-sm">
                      Disputes (chargebacks)
                    </strong>
                  </td>
                  <td className="p-3 align-top text-systemLabel text-sm">
                    Likely handled through your gateway.
                  </td>
                  <td className="p-3 align-top text-systemLabel text-sm">
                    Be prepared with order documentation.
                  </td>
                </tr>
                <tr className="bg-gray-400/10 transition-colors">
                  <td className="p-3 align-top w-1/3 border-r border-systemBorder">
                    <strong className="text-systemTitle text-sm">
                      Multi-currency setup
                    </strong>
                  </td>
                  <td className="p-3 align-top text-systemLabel text-sm">
                    Ensure your payment provider supports it.
                  </td>
                  <td className="p-3 align-top text-systemLabel text-sm">
                    Test with sandbox mode before going live.
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </section>

        {/* Summary Section */}
        <section id="summary" className="mb-6 scroll-mt-20">
          <h2 className="text-sm font-semibold text-systemTitle mb-1">
            Summary
          </h2>

          <div className="bg-systemBackground border border-systemBorder rounded-lg p-4 mt-2">
            <p className="text-sm text-systemLabel">
              xPage offers flexible payment options—whether you want built-in
              processing or third-party integration. You control how your store
              gets paid, with secure methods, real-time tracking, and
              configurable settings.
            </p>
          </div>
        </section>

        {/* Process Payments Overview Section */}
        <section id="process-payments-overview" className="mb-6 scroll-mt-20">
          <h2 className="text-sm font-semibold text-systemTitle mb-1">
            Process payments with xPage
          </h2>

          <div className="bg-systemBackground border border-systemBorder rounded-lg p-4 mt-2">
            <p className="text-sm text-systemLabel">
              With xPage, you can accept payments seamlessly and securely across
              multiple methods. Here's how payment processing typically works on
              the platform:
            </p>
          </div>
        </section>

        {/* Payment Processing Workflow Section */}
        <section id="payment-processing-workflow" className="mb-6 scroll-mt-20">
          <h2 className="text-sm font-semibold text-systemTitle mb-1">
            How payment processing works
          </h2>

          <div className="bg-systemBackground border border-systemBorder rounded-lg p-4 mt-2">
            <p className="text-sm text-systemLabel mb-4">
              When a customer checks out:
            </p>

            <ol className="list-decimal list-inside space-y-1 text-sm text-systemLabel ml-4">
              <li className="mb-2">
                <span className="font-medium">
                  <strong>Checkout Initiation:</strong> They enter their payment
                  details and click to pay.
                </span>
              </li>
              <li className="mb-2">
                <span className="font-medium">
                  <strong>Payment Processing:</strong> xPage routes the
                  transaction through third-party gateways (like Stripe or
                  PayPal): xPage redirects or integrates via API/webhooks.
                </span>
              </li>
              <li className="mb-2">
                <span className="font-medium">
                  <strong>Transaction Outcome:</strong> Upon approval: The
                  system updates the order status (e.g. "Paid" or "Pending").
                  Both you and the customer receive confirmation notifications.
                </span>
              </li>
              <li className="mb-2">
                <span className="font-medium">
                  <strong>Payout Transfer:</strong> Funds are transferred
                  according to your chosen provider's payout schedule, this
                  might involve a delay depending on settlement times.
                </span>
              </li>
            </ol>
          </div>
        </section>

        {/* Payment Flows Section */}
        <section id="payment-flows-setups" className="mb-6 scroll-mt-20">
          <h2 className="text-sm font-semibold text-systemTitle mb-1">
            Payment flows for different setups
          </h2>

          <div className="bg-systemBackground border border-systemBorder rounded-lg p-4 mt-2">
            <table className="w-full mb-6">
              <tbody className="border divide-y divide-systemBorder">
                <tr className="bg-gray-400/10 transition-colors">
                  <td className="p-3 align-top w-1/3 border-r border-systemBorder">
                    <strong className="text-systemTitle text-sm">
                      Setup Type
                    </strong>
                  </td>
                  <td className="p-3 align-top text-systemLabel text-sm">
                    <strong className="text-systemTitle text-sm">
                      How it Works
                    </strong>
                  </td>
                </tr>
                <tr className="">
                  <td className="p-3 align-top w-1/3 border-r border-systemBorder">
                    <strong className="text-systemTitle text-sm">
                      External Gateway Integration
                    </strong>
                  </td>
                  <td className="p-3 align-top text-systemLabel text-sm">
                    Upon checkout, xPage redirects or communicates with the
                    external provider via API; xPage updates order status via
                    webhook when the transaction completes.
                  </td>
                </tr>
                <tr className="bg-gray-400/10 transition-colors">
                  <td className="p-3 align-top w-1/3 border-r border-systemBorder">
                    <strong className="text-systemTitle text-sm">
                      Manual Methods (e.g., COD)
                    </strong>
                  </td>
                  <td className="p-3 align-top text-systemLabel text-sm">
                    Order is created, but marked as "Pending" until manual
                    payment confirmation by the merchant.
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </section>

        {/* Handling Payment Failures Section */}
        <section id="handling-payment-failures" className="mb-6 scroll-mt-20">
          <h2 className="text-sm font-semibold text-systemTitle mb-1">
            Handling payment failures
          </h2>

          <div className="bg-systemBackground border border-systemBorder rounded-lg p-4 mt-2">
            <p className="text-sm text-systemLabel mb-4">If a payment fails:</p>

            <ul className="list-disc list-inside space-y-1 text-sm text-systemLabel ml-4 mb-4">
              <li>
                The order is marked accordingly (e.g., "Failed" or "Payment
                Required").
              </li>
              <li>You'll receive a notification to take action.</li>
              <li>
                The customer is prompted to retry or update their payment
                method.
              </li>
            </ul>

            <p className="text-sm text-systemLabel mb-4">
              To handle these smoothly:
            </p>

            <ul className="list-disc list-inside space-y-1 text-sm text-systemLabel ml-4">
              <li>Use clear error messaging.</li>
              <li>Make retrying easy for customers.</li>
              <li>
                Enable logs or dashboards to monitor failed attempts and resolve
                them faster.
              </li>
            </ul>
          </div>
        </section>

        {/* Settlement and Dispute Management Section */}
        <section
          id="settlement-dispute-management"
          className="mb-6 scroll-mt-20"
        >
          <h2 className="text-sm font-semibold text-systemTitle mb-1">
            Settlement and dispute management
          </h2>

          <div className="bg-systemBackground border border-systemBorder rounded-lg p-4 mt-2">
            <ul className="list-disc list-inside space-y-1 text-sm text-systemLabel ml-4">
              <li>
                <strong>Payouts:</strong> Funds from successful transactions are
                sent to your assigned bank or account based on the provider's
                payout schedule.
              </li>
              <li>
                <strong>Refunds:</strong> Can typically be issued via the
                payment provider dashboard or xPage admin (if supported).
              </li>
              <li>
                <strong>Disputes / Chargebacks:</strong> Managed through your
                payment provider; make sure you have accurate order records and
                communication logs if you need to contest a chargeback.
              </li>
            </ul>
          </div>
        </section>

        {/* Security & Compliance Section 2 */}
        <section id="security-compliance-2" className="mb-6 scroll-mt-20">
          <h2 className="text-sm font-semibold text-systemTitle mb-1">
            Security & compliance
          </h2>

          <div className="bg-systemBackground border border-systemBorder rounded-lg p-4 mt-2">
            <ul className="list-disc list-inside space-y-1 text-sm text-systemLabel ml-4">
              <li>
                <strong>PCI Compliance:</strong> xPage adheres to data security
                standards or offloads compliance to your chosen provider via
                hosted checkout.
              </li>
              <li>
                <strong>SSL Encryption:</strong> All checkout flows are secured
                over HTTPS for safe data transmission.
              </li>
              <li>
                <strong>PCI-DSS:</strong> If you're using an API-based external
                gateway you may need to ensure PCI compliance depending on your
                setup.
              </li>
            </ul>
          </div>
        </section>

        {/* Summary Section 2 */}
        <section id="summary-2" className="scroll-mt-20">
          <h2 className="text-sm font-semibold text-systemTitle mb-1">
            Summary
          </h2>

          <div className="bg-systemBackground border border-systemBorder rounded-lg p-4 mt-2">
            <p className="text-sm text-systemLabel">
              xPage offers flexible and secure payment processing options;
              whether you use its built-in solution or integrate external
              gateways, the platform handles the flow from checkout to payout.
              It updates order statuses in real time, protects user data, and
              provides tools to manage refunds and disputes.
            </p>
          </div>
        </section>
      </div>

      {/* Table of Contents Sidebar */}
      <div className="lg:w-64 flex-shrink-0 lg:block hidden">
        <div className="sticky top-6 bg-systemBackground border border-systemBorder rounded-lg p-4">
          <h3 className="text-systemTitle text-sm font-semibold mb-3">
            On this page
          </h3>
          <ul className="text-systemLabel text-sm space-y-2">
            <li>
              <a
                href="#overview"
                className={`block py-1 transition-colors border-l-2 pl-3 ${
                  activeSection === "overview"
                    ? "text-deepPurple font-semibold border-deepPurple"
                    : "border-transparent hover:text-deepPurple"
                }`}
              >
                Overview
              </a>
            </li>
            <li>
              <a
                href="#available-payment-methods"
                className={`block py-1 transition-colors border-l-2 pl-3 ${
                  activeSection === "available-payment-methods"
                    ? "text-deepPurple font-semibold border-deepPurple"
                    : "border-transparent hover:text-deepPurple"
                }`}
              >
                Available Payment Methods
              </a>
            </li>
            <li>
              <a
                href="#setup-payment-methods"
                className={`block py-1 transition-colors border-l-2 pl-3 ${
                  activeSection === "setup-payment-methods"
                    ? "text-deepPurple font-semibold border-deepPurple"
                    : "border-transparent hover:text-deepPurple"
                }`}
              >
                Set Up Payment Methods
              </a>
            </li>
            <li>
              <a
                href="#managing-payments"
                className={`block py-1 transition-colors border-l-2 pl-3 ${
                  activeSection === "managing-payments"
                    ? "text-deepPurple font-semibold border-deepPurple"
                    : "border-transparent hover:text-deepPurple"
                }`}
              >
                Managing Payments
              </a>
            </li>
            <li>
              <a
                href="#security-compliance"
                className={`block py-1 transition-colors border-l-2 pl-3 ${
                  activeSection === "security-compliance"
                    ? "text-deepPurple font-semibold border-deepPurple"
                    : "border-transparent hover:text-deepPurple"
                }`}
              >
                Security & Compliance
              </a>
            </li>
            <li>
              <a
                href="#common-issues-tips"
                className={`block py-1 transition-colors border-l-2 pl-3 ${
                  activeSection === "common-issues-tips"
                    ? "text-deepPurple font-semibold border-deepPurple"
                    : "border-transparent hover:text-deepPurple"
                }`}
              >
                Common Issues & Tips
              </a>
            </li>
            <li>
              <a
                href="#summary"
                className={`block py-1 transition-colors border-l-2 pl-3 ${
                  activeSection === "summary"
                    ? "text-deepPurple font-semibold border-deepPurple"
                    : "border-transparent hover:text-deepPurple"
                }`}
              >
                Summary
              </a>
            </li>
            <li>
              <a
                href="#process-payments-overview"
                className={`block py-1 transition-colors border-l-2 pl-3 ${
                  activeSection === "process-payments-overview"
                    ? "text-deepPurple font-semibold border-deepPurple"
                    : "border-transparent hover:text-deepPurple"
                }`}
              >
                Process Payments Overview
              </a>
            </li>
            <li>
              <a
                href="#payment-processing-workflow"
                className={`block py-1 transition-colors border-l-2 pl-3 ${
                  activeSection === "payment-processing-workflow"
                    ? "text-deepPurple font-semibold border-deepPurple"
                    : "border-transparent hover:text-deepPurple"
                }`}
              >
                Payment Processing Workflow
              </a>
            </li>
            <li>
              <a
                href="#payment-flows-setups"
                className={`block py-1 transition-colors border-l-2 pl-3 ${
                  activeSection === "payment-flows-setups"
                    ? "text-deepPurple font-semibold border-deepPurple"
                    : "border-transparent hover:text-deepPurple"
                }`}
              >
                Payment Flows
              </a>
            </li>
            <li>
              <a
                href="#handling-payment-failures"
                className={`block py-1 transition-colors border-l-2 pl-3 ${
                  activeSection === "handling-payment-failures"
                    ? "text-deepPurple font-semibold border-deepPurple"
                    : "border-transparent hover:text-deepPurple"
                }`}
              >
                Handling Payment Failures
              </a>
            </li>
            <li>
              <a
                href="#settlement-dispute-management"
                className={`block py-1 transition-colors border-l-2 pl-3 ${
                  activeSection === "settlement-dispute-management"
                    ? "text-deepPurple font-semibold border-deepPurple"
                    : "border-transparent hover:text-deepPurple"
                }`}
              >
                Settlement & Disputes
              </a>
            </li>
            <li>
              <a
                href="#security-compliance-2"
                className={`block py-1 transition-colors border-l-2 pl-3 ${
                  activeSection === "security-compliance-2"
                    ? "text-deepPurple font-semibold border-deepPurple"
                    : "border-transparent hover:text-deepPurple"
                }`}
              >
                Security & Compliance
              </a>
            </li>
            <li>
              <a
                href="#summary-2"
                className={`block py-1 transition-colors border-l-2 pl-3 ${
                  activeSection === "summary-2"
                    ? "text-deepPurple font-semibold border-deepPurple"
                    : "border-transparent hover:text-deepPurple"
                }`}
              >
                Summary
              </a>
            </li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default GetPayments;
